import { lazy, Suspense, useContext } from "react";
import { Routes, Route, Navigate } from "react-router";
import RequirePermission from "../components/RequirePermission";
import { Loading } from "../components/Loading";
import ErrorPage from "../components/ErrorPage";
import CreateRole from "../pages/roles/CreateRole";
import RolesList from "../pages/roles/RolesList";
import RoleDetail from "../pages/roles/RoleDetail";
import EditRole from "../pages/roles/EditRole";
import CreateUser from "../pages/users/CreateUser";
import UsersList from "../pages/users/UsersList";
import EditUser from "../pages/users/EditUser";
import CreateAdmin from "../pages/admin/CreateNewAdmin";
import AdminsList from "../pages/admin/AdminsList";
import AdminDetail from "../pages/admin/AdminDetail";
import EditAdmin from "../pages/admin/EditAdmin";
import CreatePackages from "../pages/management/package/CreatePackages";
import Vehicles from "../pages/management/vehicle/Vehicles";
import AddVehicle from "../pages/management/vehicle/AddVehicle";
import EditVehicle from "../pages/management/vehicle/EditVehicle";
import CustomBooking from "../pages/management/customBooking/CustomBooking";
import ViewTaxes from "../pages/management/taxes/ViewTaxes";
import Fleets from "../pages/inventory/fleet/Fleet";
import Drivers from "../pages/inventory/driver/Drivers";
import Vendors from "../pages/inventory/vendor/Vendors";
import AddFleet from "../pages/inventory/fleet/AddFleet";

const Settings = lazy(() => import("../pages/settings/Settings"));
const Home = lazy(() => import("../pages/home/Home"));
import Leads from "../pages/leads/Leads";
import LeadsList from "../pages/leads/LeadsList";
import AddLead from "../pages/leads/AddLead";
import Results from "../pages/leads/Results";
import PendingTokenDetails from "../pages/leads/PendingTokenDetails";
import Booking from "../pages/leads/Booking";
import Packages from "../pages/leads/Packages";
import DetailsPage from "../pages/leads/DetailsPage";
import CustomBook from "../pages/leads/CustomBook";
import BookingDetails from "../pages/leads/BookingDetails";
import BookingStatus from "../pages/leads/BookingStatus";
import BookingConfirmed from "../pages/leads/BookingConfirmed";
import History from "../pages/leads/History";
import Management from "../pages/management/Management";
import PendingRequests from "../pages/management/pending-requests/Requests";
import EditPackages from "../pages/management/package/EditPackage";
import ViewPackages from "../pages/management/package/ViewPackages";
import DetailPackage from "../pages/management/package/DetailPackage";
import AddDriver from "../pages/inventory/driver/AddDriver";
import AddVendor from "../pages/inventory/vendor/AddVendor";
import EditDriver from "../pages/inventory/driver/EditDriver";
import EditVendor from "../pages/inventory/vendor/EditVendor";
import EditFleet from "../pages/inventory/fleet/EditFleet";
import PendingDriverList from "../pages/management/pending-requests/drivers/PendingList";
import PendingFleetList from "../pages/management/pending-requests/fleets/PendingList";
import PendingVendorList from "../pages/management/pending-requests/vendors/PendingList";
import VehicleDetails from "../pages/management/vehicle/VehicelDetails";
import Customer from "../pages/management/customer/customers";
import CustomerDetails from "../pages/management/customer/customerdetails";
import CustomerEdit from "../pages/management/customer/customeredit";
import AddCustomer from "../pages/management/customer/addcustomer";
import UserDetail from "../pages/users/UserDetail";
import LeadDetails from "../pages/leads/LeadDetails";
import EditLead from "../pages/leads/EditLead";
import EditBooking from "../pages/leads/EditBooking";
import EditPendingToken from "../pages/leads/EditPendingToken";
import Dispatch from "../pages/dispatch/Dispatch";
import AvailableDriver from "../pages/dispatch/AvailableDriver";
import AllDrivers from "../pages/dispatch/AllDrivers";
import Customers from "../pages/finance/customer/Customer";
import CustomerFinanceDetails from "../pages/finance/customer/CustomerDetails";
import DriverAccount from "../pages/finance/driver/DriverAccount";
import DriverVendor from "../pages/finance/vendor/VendorAccount";
import FleetEach from "../pages/inventory/navigatePages/fleetEach";
import VendorEach from "../pages/inventory/navigatePages/vendorEach";
import DriverEach from "../pages/inventory/navigatePages/driverEach";
import Stats from "../pages/stats/Stats";
import CustomerPolicies from "../pages/customer/CustomerPolicies";
import CreateReport from "../pages/reports/CreateReport";
import ViewReport from "../pages/reports/ViewReport";
import ReportDetail from "../pages/reports/ReportDetail";
import InvoiceReceipts from "../pages/finance/invoiceReceipts/InvoiceReceipts";
import AuditLogs from "../pages/settings/AuditLogs";
import TermsAndConditions from "../pages/management/termsAndConditions/TermsAndConditions";
import GlobalFields from "../pages/management/Globalfields/globalfields";

// Verification Center
import VerificationCenter from "../pages/verification/VerificationCenter";
import DriverApprovals from "../pages/verification/driver-approvals/DriverApprovals";
import VehicleApprovals from "../pages/verification/vehicle-approvals/VehicleApprovals";
import VendorApprovals from "../pages/verification/vendor-approvals/VendorApprovals";
import DriverApprovalDetails from "../pages/verification/driver-approvals/DriverApprovalDetails";
import VehicleApprovalDetails from "../pages/verification/vehicle-approvals/VehicleApprovalDetails";
import VendorApprovalDetails from "../pages/verification/vendor-approvals/VendorApprovalDetails";
import AwaitingClarification from "../pages/verification/awaiting-clarification/AwaitingClarification";
import VendorOnboardingConfig from "../pages/verification/vendor-onboarding-config/VendorOnboardingConfig";

// Support Center
import SupportCenter from "../pages/support/SupportCenter";
import Complaints from "../pages/support/complaints/Complaints";
import FAQs from "../pages/support/faqs/FAQs";
import KnowledgeBase from "../pages/support/knowledge-base/KnowledgeBase";
import TrainingVideos from "../pages/support/training-videos/TrainingVideos";

// Communication
import Communication from "../pages/communication/Communication";

// System Logs
import SystemLogs from "../pages/system-logs/SystemLogs";
import TicketDetails from "../pages/support/complaints/TicketDetails";
import { AuthContext } from "./AuthValidationLayer";

const RouteSegmentProvider = () => {
  const { hasPermission } = useContext(AuthContext);

  const hasAnySupportCenterAccess =
    hasPermission("Support Center", "Complaints", "GET") ||
    hasPermission("Support Center", "FAQs", "GET") ||
    hasPermission("Support Center", "Knowledge Base", "GET") ||
    hasPermission("Support Center", "Training Videos", "GET");

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route
          path="/bookings"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <Leads />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <LeadsList />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/edit/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="PATCH"
            >
              <EditBooking />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/pending-token/edit/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="PATCH"
            >
              <EditPendingToken />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead/edit/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="PATCH"
            >
              <EditLead />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead/search"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="POST"
            >
              <AddLead />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/packages"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="POST"
            >
              <Packages />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead/results"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <Results />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead/add-booking"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="POST"
            >
              <BookingDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead/booking-details"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <Booking />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead/booking-status"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <BookingStatus />
            </RequirePermission>
          }
        />
        <Route
          path="/history"
          element={
            <RequirePermission
              heading="General"
              subHeading="Booking History"
              method="GET"
            >
              <History />
            </RequirePermission>
          }
        />
        <Route
          path="/stats"
          element={
            <RequirePermission
              heading="General"
              subHeading="Stats"
              method="GET"
            >
              <Stats />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/create"
          element={
            <RequirePermission
              heading="General"
              subHeading="Create Report"
              method="POST"
            >
              <CreateReport />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/edit/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Create Report"
              method="POST"
            >
              <CreateReport />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/view"
          element={
            <RequirePermission
              heading="General"
              subHeading="View Report"
              method="GET"
            >
              <ViewReport />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/view/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="View Report"
              method="GET"
            >
              <ReportDetail />
            </RequirePermission>
          }
        />
        <Route
          path="/customer-policies"
          element={
            <RequirePermission
              heading="General"
              subHeading="Customer Policies"
              method="GET"
            >
              <CustomerPolicies />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <LeadDetails />
            </RequirePermission>
          }
        />
        {/* <Route
          path="/bookings/custom/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <CustomBookingDetails />
            </RequirePermission>
          }
        /> */}
        <Route
          path="/history/lead/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Booking History"
              method="GET"
            >
              <LeadDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/leads/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <LeadDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/pending/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <PendingTokenDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead/booking-confirmed"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <BookingConfirmed />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/lead/custom-book"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="POST"
            >
              <CustomBook />
            </RequirePermission>
          }
        />
        <Route path="/management" element={<Management />} />
        <Route
          path="/management/packages/edit/:id"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Tour Package Management"
              method="PATCH"
            >
              <EditPackages />
            </RequirePermission>
          }
        />
        <Route
          path="/management/packages/:id"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Tour Package Management"
              method="GET"
            >
              <DetailPackage />
            </RequirePermission>
          }
        />
        <Route
          path="/management/packages"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Tour Package Management"
              method="GET"
            >
              <ViewPackages />
            </RequirePermission>
          }
        />
        <Route
          path="/management/packages/create"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Tour Package Management"
              method="POST"
            >
              <CreatePackages />
            </RequirePermission>
          }
        />
        <Route
          path="/management/vehicles"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Vehicle Segments"
              method="GET"
            >
              <Vehicles />
            </RequirePermission>
          }
        />
        <Route
          path="/management/vehicles/create"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Vehicle Segments"
              method="POST"
            >
              <AddVehicle />
            </RequirePermission>
          }
        />
        <Route
          path="/management/vehicles/edit/:id"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Vehicle Segments"
              method="PATCH"
            >
              <EditVehicle />
            </RequirePermission>
          }
        />
        <Route
          path="/management/vehicles/:id"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Vehicle Segments"
              method="GET"
            >
              <VehicleDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/management/customer"
          element={
            <RequirePermission
              heading="General"
              subHeading="Customer List"
              method="GET"
            >
              <Customer />
            </RequirePermission>
          }
        />
        <Route
          path="/customer"
          element={
            <RequirePermission
              heading="General"
              subHeading="Customer List"
              method="GET"
            >
              <Customer />
            </RequirePermission>
          }
        />
        <Route
          path="/management/customer/create"
          element={
            <RequirePermission
              heading="General"
              subHeading="Customer List"
              method="POST"
            >
              <AddCustomer />
            </RequirePermission>
          }
        />
        <Route
          path="/management/customer/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Customer List"
              method="GET"
            >
              <CustomerDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/management/customer/edit/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Customer List"
              method="PATCH"
            >
              <CustomerEdit />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/vehicles"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vehicles"
              method="GET"
            >
              <Fleets />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/vehicles/add"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vehicles"
              method="POST"
            >
              <AddFleet />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/vehicles/edit/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vehicles"
              method="PATCH"
            >
              <EditFleet />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/vehicles/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vehicles"
              method="GET"
            >
              <FleetEach />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/driver"
          element={
            <RequirePermission
              heading="General"
              subHeading="Drivers"
              method="GET"
            >
              <Drivers />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/driver/add"
          element={
            <RequirePermission
              heading="General"
              subHeading="Drivers"
              method="POST"
            >
              <AddDriver />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/driver/edit/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Drivers"
              method="PATCH"
            >
              <EditDriver />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/driver/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Drivers"
              method="GET"
            >
              <DriverEach />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/vendor"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vendors"
              method="GET"
            >
              <Vendors />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/vendor/add"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vendors"
              method="POST"
            >
              <AddVendor />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/vendor/edit/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vendors"
              method="PATCH"
            >
              <EditVendor />
            </RequirePermission>
          }
        />
        <Route
          path="/fleet-management/vendor/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vendors"
              method="GET"
            >
              <VendorEach />
            </RequirePermission>
          }
        />
        <Route
          path="/management/taxes"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="State Tax List"
              method="GET"
            >
              <ViewTaxes />
            </RequirePermission>
          }
        />
        <Route
          path="/management/custom-booking"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Custom Booking"
              method="GET"
            >
              <CustomBooking />
            </RequirePermission>
          }
        />
        {/* <Route
          path="/bookings/custom-booking/list"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="GET"
            >
              <CustomBookingList />
            </RequirePermission>
          }
        />
        <Route
          path="/bookings/custom-booking/edit/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Leads"
              method="PATCH"
            >
              <CustomBooking />
            </RequirePermission>
          }
        /> */}
        <Route
          path="/management/pending-requests"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vehicles"
              method="GET"
            >
              <PendingRequests />
            </RequirePermission>
          }
        />
        <Route
          path="/management/pending-requests/drivers"
          element={
            <RequirePermission
              heading="General"
              subHeading="Drivers"
              method="GET"
            >
              <PendingDriverList />
            </RequirePermission>
          }
        />
        <Route
          path="/management/pending-requests/fleets"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vehicles"
              method="GET"
            >
              <PendingFleetList />
            </RequirePermission>
          }
        />
        <Route
          path="/management/pending-requests/vendors"
          element={
            <RequirePermission
              heading="General"
              subHeading="Vendors"
              method="GET"
            >
              <PendingVendorList />
            </RequirePermission>
          }
        />
        <Route
          path="/dispatch"
          element={
            <RequirePermission
              heading="General"
              subHeading="Fleet Management"
              method="GET"
            >
              <Dispatch />
            </RequirePermission>
          }
        />
        <Route
          path="/dispatch/available-drivers"
          element={
            <RequirePermission
              heading="General"
              subHeading="Fleet Management"
              method="GET"
            >
              <AvailableDriver />
            </RequirePermission>
          }
        />
        <Route
          path="/dispatch/all-drivers"
          element={
            <RequirePermission
              heading="General"
              subHeading="Fleet Management"
              method="GET"
            >
              <AllDrivers />
            </RequirePermission>
          }
        />
        <Route
          path="/accounts"
          element={
            <RequirePermission
              heading="General"
              subHeading="Finance Customer"
              method="GET"
            >
              <Customers />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/customer"
          element={
            <RequirePermission
              heading="General"
              subHeading="Finance Customer"
              method="GET"
            >
              <Customers />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/customer/:id"
          element={
            <RequirePermission
              heading="General"
              subHeading="Finance Customer"
              method="GET"
            >
              <CustomerFinanceDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/driver"
          element={
            <RequirePermission
              heading="General"
              subHeading="Finance Driver"
              method="GET"
            >
              <DriverAccount />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/vendor"
          element={
            <RequirePermission
              heading="General"
              subHeading="Finance Vendor"
              method="GET"
            >
              <DriverVendor />
            </RequirePermission>
          }
        />
        <Route path="/settings" element={<Settings />} />
        <Route
          path="/settings/user/create"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Users"
              method="POST"
            >
              <CreateUser />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/user/manage"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Users"
              method="GET"
            >
              <UsersList />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/user/manage/:id"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Users"
              method="PATCH"
            >
              <EditUser />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/user/:id"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Users"
              method="GET"
            >
              <UserDetail />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/admin/create"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Employees"
              method="POST"
            >
              <CreateAdmin />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/admin/manage"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Employees"
              method="GET"
            >
              <AdminsList />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/admin/:id"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Employees"
              method="GET"
            >
              <AdminDetail />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/admin/edit/:id"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Employees"
              method="PATCH"
            >
              <EditAdmin />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/roles/create"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Roles"
              method="POST"
            >
              <CreateRole />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/roles/manage"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Roles"
              method="GET"
            >
              <RolesList />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/roles/view/:id"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Roles"
              method="GET"
            >
              <RoleDetail />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/roles/edit/:id"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Roles"
              method="PATCH"
            >
              <EditRole />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/invoice-receipts"
          element={
            <RequirePermission
              heading="General"
              subHeading="Generate Invoice and Receipts"
              method="GET"
            >
              <InvoiceReceipts />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/audit-logs"
          element={
            <RequirePermission
              heading="Access Control"
              subHeading="Audit Logs"
              method="GET"
            >
              <AuditLogs />
            </RequirePermission>
          }
        />
        <Route
          path="/management/terms-and-conditions"
          element={
            <RequirePermission
              heading="Operations Setup"
              subHeading="Terms and Conditions"
              method="GET"
            >
              <TermsAndConditions />
            </RequirePermission>
          }
        />
        <Route path="/management/global-fields" element={<GlobalFields />} />

        {/* Verification Center Routes */}
        <Route path="/verification" element={<VerificationCenter />} />
        <Route
          path="/verification/driver-approvals"
          element={
            <RequirePermission
              heading="Verification Center"
              subHeading="Driver Approvals"
              method="GET"
            >
              <DriverApprovals />
            </RequirePermission>
          }
        />
        <Route
          path="/verification/driver-approvals/:id"
          element={
            <RequirePermission
              heading="Verification Center"
              subHeading="Driver Approvals"
              method="GET"
            >
              <DriverApprovalDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/verification/vehicle-approvals"
          element={
            <RequirePermission
              heading="Verification Center"
              subHeading="Vehicle Approvals"
              method="GET"
            >
              <VehicleApprovals />
            </RequirePermission>
          }
        />
        <Route
          path="/verification/vehicle-approvals/:id"
          element={
            <RequirePermission
              heading="Verification Center"
              subHeading="Vehicle Approvals"
              method="GET"
            >
              <VehicleApprovalDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/verification/vendor-approvals"
          element={
            <RequirePermission
              heading="Verification Center"
              subHeading="Vendor Approvals"
              method="GET"
            >
              <VendorApprovals />
            </RequirePermission>
          }
        />
        <Route
          path="/verification/vendor-approvals/:id"
          element={
            <RequirePermission
              heading="Verification Center"
              subHeading="Vendor Approvals"
              method="GET"
            >
              <VendorApprovalDetails />
            </RequirePermission>
          }
        />
        <Route
          path="/verification/awaiting-clarification"
          element={
            <RequirePermission
              heading="Verification Center"
              subHeading="Awaiting Clarification"
              method="GET"
            >
              <AwaitingClarification />
            </RequirePermission>
          }
        />
        <Route
          path="/verification/vendor-onboarding-config"
          element={
            <RequirePermission
              heading="Verification Center"
              subHeading="Vendor Onboarding Config"
              method="GET"
            >
              <VendorOnboardingConfig />
            </RequirePermission>
          }
        />

        {/* Support Center Routes */}
        <Route
          path="/support"
          element={
            hasAnySupportCenterAccess ? <SupportCenter /> : <Navigate to="/" replace />
          }
        />
        <Route
          path="/support/complaints"
          element={
            <RequirePermission
              heading="Support Center"
              subHeading="Complaints"
              method="GET"
            >
              <Complaints />
            </RequirePermission>
          }
        />
        <Route
          path="/support/complaints/:id"
          element={
            <RequirePermission
              heading="Support Center"
              subHeading="Complaints"
              method="GET"
            >
              <TicketDetails />
            </RequirePermission>
          }
        />

        <Route
          path="/support/faqs"
          element={
            <RequirePermission
              heading="Support Center"
              subHeading="FAQs"
              method="GET"
            >
              <FAQs />
            </RequirePermission>
          }
        />
        <Route
          path="/support/knowledge-base"
          element={
            <RequirePermission
              heading="Support Center"
              subHeading="Knowledge Base"
              method="GET"
            >
              <KnowledgeBase />
            </RequirePermission>
          }
        />
        <Route
          path="/support/training-videos"
          element={
            <RequirePermission
              heading="Support Center"
              subHeading="Training Videos"
              method="GET"
            >
              <TrainingVideos />
            </RequirePermission>
          }
        />

        {/* Communication Routes */}
        <Route
          path="/communication"
          element={
            <RequirePermission
              heading="Communication"
              subHeading="Communication"
              method="GET"
            >
              <Communication />
            </RequirePermission>
          }
        />

        {/* System Logs Routes */}
        <Route
          path="/system-logs"
          element={
            <RequirePermission
              heading="System Logs"
              subHeading="System Logs"
              method="GET"
            >
              <SystemLogs />
            </RequirePermission>
          }
        />

        {/* generic route LAST */}
        <Route path="/:type/:id" element={<DetailsPage />} />
        <Route path="/*" element={<ErrorPage />} />
      </Routes>
    </Suspense>
  );
};

export default RouteSegmentProvider;
